import {
  IVacationNfaBuild,
  IVacationNfaConflict,
  IVacationNfaCrewStatus,
  VacationNfaBuildStatus,
  VacationNfaConflictReason,
  VacationNfaSyncStatus,
  VacationNfaSyncTrigger,
} from "@vmosolutions/oc-interfaces";
import moment from "moment";

import { crewName } from "../Awarding/awardingHelpers";
import { formatVacationDate } from "../helpers";

/** How often the build status is re-read while a build runs. */
export const NFA_BUILD_POLL_MS = 3000;

/** A running build that has not moved for this long has most likely stopped. */
export const NFA_BUILD_STALLED_MS = 2 * 60 * 1000;

/** How long after it started the server gives up on a build still marked running, as `STALE_BUILD_MS` there. */
export const NFA_BUILD_GIVEN_UP_MS = 17 * 60 * 1000;

export const isNfaBuildRunning = (build: IVacationNfaBuild | null | undefined): boolean =>
  build?.status === VacationNfaBuildStatus.Running;

/** True when the build ended before every crew member was done, so it needs running again. */
export const isNfaBuildStopped = (build: IVacationNfaBuild | null | undefined): boolean =>
  build?.status === VacationNfaBuildStatus.Incomplete || build?.status === VacationNfaBuildStatus.Failed;

/** The button that opens the last build. A build that stopped says so, so it is noticed without opening it. */
export const nfaBuildButtonLabel = (build: IVacationNfaBuild): string => {
  if (isNfaBuildRunning(build)) return "Building NFAs...";
  if (build.status === VacationNfaBuildStatus.Failed) return "NFA Build Failed";
  if (build.status === VacationNfaBuildStatus.Incomplete) return "NFA Build Stopped";
  return "Last NFA Build";
};

/**
 * Why a running build that has not moved for a while may have stopped, and when it can be run again; null
 * while it is moving. A worker that dies cannot mark the build, so it is only given up on later.
 */
export const nfaBuildStalledLine = (build: IVacationNfaBuild, now: Date): string | null => {
  if (!isNfaBuildRunning(build)) return null;
  const last = moment(build.progressAt ?? build.claimedAt ?? build.startedAt);
  if (now.valueOf() - last.valueOf() < NFA_BUILD_STALLED_MS) return null;
  const givenUp = moment(build.startedAt).add(NFA_BUILD_GIVEN_UP_MS, "milliseconds");
  return `No progress since ${last.format("HH:mm")}. If the build has stopped, it can be run again from ${givenUp.format("HH:mm")}.`;
};

export const nfaBuildStatusLabel = (build: IVacationNfaBuild | null | undefined): string => {
  switch (build?.status) {
    case VacationNfaBuildStatus.Running:
      return "Building";
    case VacationNfaBuildStatus.Complete:
      return "Complete";
    case VacationNfaBuildStatus.Incomplete:
      return "Stopped before it finished";
    case VacationNfaBuildStatus.Failed:
      return "Failed";
    default:
      return "Not built yet";
  }
};

/** How far through its crew the build is, 0 to 100. */
export const nfaBuildProgress = (build: IVacationNfaBuild | null | undefined): number =>
  build?.crewTotal ? Math.min(100, Math.round((build.crewProcessed / build.crewTotal) * 100)) : 0;

const plural = (count: number, one: string, many = `${one}s`): string => `${count} ${count === 1 ? one : many}`;

/** One statement per line about what a build did. A build no worker has picked up yet has nothing to count. */
export const nfaBuildSummaryLines = (build: IVacationNfaBuild): string[] =>
  isNfaBuildRunning(build) && !build.claimedAt
    ? ["Waiting to start."]
    : [
        `${build.crewProcessed} of ${plural(build.crewTotal, "crew member")} done.`,
        `${plural(build.nfasCreated, "NFA")} created, ${build.nfasRemoved} removed.`,
        `${build.crewBuilt} built, ${build.crewWithConflicts} with conflicts, ${build.crewFailed} failed, ${build.crewSkipped} with no awards.`,
      ];

const conflictReasonLabel: Record<VacationNfaConflictReason, string> = {
  [VacationNfaConflictReason.Foreign]: "not written by the build",
  [VacationNfaConflictReason.Planned]: "a planned NFA",
};

/** A day the build left alone, as one line. */
export const conflictLine = (conflict: IVacationNfaConflict): string =>
  `${formatVacationDate(conflict.date)}: ${conflict.existingTitle} is already there (${conflictReasonLabel[conflict.reason]})`;

/** A crew member whose last sync failed or left days alone, ready to list. */
export interface INfaBuildProblem {
  userId: string;
  name: string;
  employeeId: string;
  failed: boolean;
  /** What happened, including when a change request rather than the build last synced them. */
  summary: string;
  conflicts: string[];
}

/** The crew members a scheduler needs to look at, in the order the status lists them. */
export const crewNeedingAttention = (crew: IVacationNfaCrewStatus[]): INfaBuildProblem[] =>
  crew.flatMap((member) => {
    const outcome = member.outcome;
    if (
      !outcome ||
      (outcome.status !== VacationNfaSyncStatus.Failed && outcome.status !== VacationNfaSyncStatus.Conflicts)
    ) {
      return [];
    }
    const failed = outcome.status === VacationNfaSyncStatus.Failed;
    const source = outcome.trigger === VacationNfaSyncTrigger.AwardsChanged ? " after a change request" : "";
    const what = failed
      ? `Nothing was built${source}. ${outcome.message ?? ""}`.trim()
      : `${plural(outcome.conflicts.length, "day")} left alone${source}.`;
    return [
      {
        userId: member.userId,
        name: crewName(member),
        employeeId: member.employeeId ?? "",
        failed,
        summary: what,
        conflicts: outcome.conflicts.map(conflictLine),
      },
    ];
  });

/** What the confirm dialog says before a build starts. */
export const nfaBuildConfirmLines = (lastBuild: IVacationNfaBuild | null | undefined): string[] => [
  "Builds vacation NFAs for every crew member from the published awards: VAC and STAT on their days off, and VGDO around each block.",
  "VAC, STAT and VGDO already on an awarded day are taken over, so they follow the award from then on.",
  "It runs in the background and takes a few minutes; you can close this window while it does.",
  ...(lastBuild
    ? [
        "Running it again writes only what is missing. It removes its NFAs for blocks a crew member no longer holds, and puts back ones that were deleted or changed to another type.",
      ]
    : []),
  "Any other NFA on an awarded day is left alone and listed in the results.",
];

export interface INfaBuildNotice {
  variant: "success" | "warning" | "error";
  message: string;
}

/** The snackbar for a build that has just finished. */
export const nfaBuildFinishedNotice = (build: IVacationNfaBuild): INfaBuildNotice => {
  if (build.status === VacationNfaBuildStatus.Failed) {
    return { variant: "error", message: `The NFA build failed. ${build.error ?? ""}`.trim() };
  }
  if (build.status === VacationNfaBuildStatus.Incomplete) {
    return { variant: "error", message: "The NFA build stopped before it finished. Run it again to carry on." };
  }
  const attention = build.crewWithConflicts + build.crewFailed;
  if (attention) {
    return {
      variant: "warning",
      message: `NFAs built. ${plural(attention, "crew member needs", "crew members need")} a look.`,
    };
  }
  return { variant: "success", message: `NFAs built for ${plural(build.crewProcessed, "crew member")}.` };
};
